// Fresh-device restore: pull the tab strip from the server chat list, seed
// local IDB records so the existing lazy-load path renders them, and fetch a
// chat's transcript (head + turns, newest tail first) on activation. Blobs
// hydrate lazily on card expand.
//
// IDB stays the local truth in front of the sync layer, so restore is
// idempotent: a server chat already present locally (matched by serverChatId)
// is left alone; only server chats missing locally are seeded, and server
// tombstones drop the local copy (cross-device delete propagation).

import { chatPersistenceEnabled } from "./featureFlags.js";
import {
    listChats,
    getChat,
    listChatTurns,
    readBlob,
} from "./chatPersistenceApi.js";
import { assembleItemsFromTurns, buildTurnHashes } from "./chatSyncSerialize.js";
import { hydrateBlobPlaceholders, hasBlobPlaceholders } from "./chatBlobExternalize.js";
import { restoreImagePreviewUrls } from "./chatSerializer.js";
import {
    loadAllChatMeta,
    loadChat,
    saveChat,
    getOutboxEntry,
    putOutboxEntry,
    deleteChat as deleteChatLocal,
    toEpochMs,
} from "./chatStorage.js";
import { initialOutbox } from "./chatOutbox.js";
import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";

const SCHEMA_VERSION = 2; // must match chatSerializer's current version

// Per-(browser, tenant) sentinel marking that a FULL server-list reconcile has
// completed at least once. Mirrors chatBackfill's once-guard. The fresh-vs-warm
// decision keys on THIS, not on whether any local chat carries a serverChatId:
// runBackfillOnce runs before the first reconcile and stamps a serverChatId onto
// every pre-existing local chat, so an upgrading device would otherwise look
// "warm" and page only once — missing chats synced from other devices past page
// one. A truly first reconcile has no sentinel → full paginate → then set it.
const RECONCILE_SENTINEL_PREFIX = "origin-chat-reconciled-v2";
const LEGACY_RECONCILE_SENTINEL_PREFIX = "origin-chat-reconciled";
function reconcileSentinelKey(tenantId) {
    discardLegacyTenantEntry(globalThis.localStorage, LEGACY_RECONCILE_SENTINEL_PREFIX, tenantId);
    return browserStorageKey(RECONCILE_SENTINEL_PREFIX, tenantId);
}
function reconcileEverCompleted(tenantId) {
    try { const key = reconcileSentinelKey(tenantId); return key ? !!localStorage.getItem(key) : false; } catch { return false; }
}
function markReconcileCompleted(tenantId) {
    try { const key = reconcileSentinelKey(tenantId); if (key) localStorage.setItem(key, "1"); } catch { /* noop */ }
}

// How many turns to pull per transcript page (newest first). Recent tail first
// so the chat is usable immediately; older turns scroll-back lazily.
const TURN_PAGE_SIZE = 50;

// Last-activity time for a restored record, taken from the SERVER's
// `updated_at` rather than the clock.
//
// `savedAt` is what `projectChatMeta` carries into `chatMeta` and what the chat
// list renders as "5m ago" (ChatConsole seeds `updatedAt: m.savedAt`). Stamping
// restore time there made every chat on a fresh device read "just now",
// collapsing the one piece of information that tells a user which conversation
// they were in the middle of. The server's value is already on both restore
// responses; it just wasn't used.
//
// Falls back to the clock only when the server sent nothing parseable, which
// keeps a missing timestamp from producing an epoch-0 record that sorts to the
// bottom forever.
function serverSavedAt(updatedAt) {
    return toEpochMs(updatedAt) ?? Date.now();
}

// Re-arm a locally-present chat whose server copy has moved ahead, so the next
// body load pulls it instead of rendering a stale local snapshot.
//
// Without this, restore is one-shot: `needsServerBody` is cleared after the
// first body load and nothing ever re-reads, so a chat continued on another
// device stayed frozen on this one until THIS device happened to write and lose
// a revision compare-and-swap. Reloading the page did nothing, which is not
// what anyone means by "my chats follow me".
//
// Two guards make the re-pull safe rather than destructive:
//
//   * Only when the server is strictly AHEAD of what we acked. Equal revisions mean we already have
//     it, and re-pulling would be pure request volume.
//   * Only when this device has nothing unsynced. A dirty outbox means local edits the server has
//     not seen, and overwriting them here would be silent data loss - that case is already handled,
//     properly, by the conflict path on the next write (`resolveConflicts`, keep-local-if-ahead).
//
// Costs no extra request: `revision` is already on the list rows this loop is
// walking.
async function refreshIfServerAhead(tenantId, localMeta, chat, refreshed) {
    const entry = await getOutboxEntry(tenantId, localMeta.localId);
    const acked = Number(entry?.ackedRevision ?? 0);
    const serverRevision = Number(chat.revision ?? 0);
    if (serverRevision <= acked) return;
    if (entry?.dirty || entry?.conflict) return;

    const raw = await loadChat(tenantId, localMeta.localId);
    if (!raw) return;
    // Belt and braces over the `dirty` check above, because re-arming triggers
    // a WHOLESALE overwrite (restoreTranscript replaces items with the
    // server's). `dirty` is the engine's own bookkeeping; if it is ever wrong,
    // trusting it alone turns this refresh into silent deletion of local turns.
    // Local content the server has never seen is therefore an independent veto:
    // that chat is behind on the WRITE side, which is the outbox's problem, not
    // something a read-side refresh may resolve by discarding it.
    const localItems = Array.isArray(raw.items) ? raw.items.length : 0;
    if (localItems > Number(chat.turnCount ?? 0)) return;
    await saveChat(tenantId, localMeta.localId, {
        ...raw,
        // The body is re-fetched on activation; until then the strip shows the
        // server's title and time rather than the stale local ones.
        title: chat.title || raw.title,
        savedAt: serverSavedAt(chat.updatedAt),
        serverTurnCount: chat.turnCount ?? raw.serverTurnCount ?? 0,
        interrupted: !!chat.interrupted,
        needsServerBody: true,
    });
    refreshed.push({ localId: localMeta.localId, serverChatId: chat.chatId, revision: serverRevision });
}

// Reconcile the server chat list into local IDB. Returns a summary of what
// changed so the caller can refresh its in-memory tab strip. `allocLocalId` is
// a caller-supplied function that returns a fresh local numeric id (respecting
// the id fence) and advances its counter — restore never invents ids itself,
// keeping the fence the single authority.
//
// Deliberately NOT startup-jittered (unlike the outbox drain): this list read is
// on the critical path — it populates the tab strip the user is waiting to see,
// so delaying it up to the jitter window would be a visible regression. The
// first-mount read burst it could contribute to is bounded instead by the
// feature flag's per-cohort rollout ramp (activation is never a single global
// flip) plus the warm-device single-page fast path below, and a paginated read
// is far cheaper for the server to shed than the write drain the jitter guards.
export async function reconcileChatList(tenantId, { allocLocalId }) {
    if (!chatPersistenceEnabled()) return { seeded: [], removed: [], refreshed: [] };

    const metas = await loadAllChatMeta(tenantId);
    const byServerId = new Map();
    for (const m of metas) {
        if (m?.serverChatId) byServerId.set(m.serverChatId, m);
    }

    // First reconcile ever on this (browser, tenant) pages the WHOLE list so it
    // can't miss chats synced from other devices past page one; once that has
    // completed we're "warm" and fetch only the most-recent page — enough to
    // surface newly-created / deleted chats — instead of paging up to tens of
    // thousands of rows on every mount. Keyed on the reconcile sentinel, NOT on
    // serverChatId presence (backfill stamps those before the first reconcile).
    const maxPages = reconcileEverCompleted(tenantId) ? 1 : 200;
    const serverChats = [];
    // Did paging finish because the server ran out of pages (exhausted), or
    // because we hit the page ceiling with more still pending (truncated)? A
    // truncated first reconcile must NOT be marked complete, or the device goes
    // "warm" and never re-pages, leaving chats past the ceiling unreachable.
    let exhausted = false;
    try {
        let pageToken = "";
        for (let page = 0; page < maxPages; page++) {
            const resp = await listChats({ includeDeleted: true, pageSize: 200, pageToken });
            serverChats.push(...resp.chats);
            if (!resp.nextPageToken) { exhausted = true; break; }
            pageToken = resp.nextPageToken;
        }
    } catch (err) {
        // Restore is best-effort; a list failure leaves local IDB as the truth.
        console.warn("[chatRestore] listChats failed:", err?.message || err);
        return { seeded: [], removed: [], refreshed: [] };
    }

    const seeded = [];
    const removed = [];
    const refreshed = [];
    for (const chat of serverChats) {
        if (!chat.chatId) continue;
        const localMeta = byServerId.get(chat.chatId);
        if (chat.deletedAt) {
            // Tombstoned elsewhere → drop the local copy if we have one.
            if (localMeta) {
                await deleteChatLocal(tenantId, localMeta.localId);
                removed.push({ localId: localMeta.localId, serverChatId: chat.chatId });
            }
            continue;
        }
        if (localMeta) {
            await refreshIfServerAhead(tenantId, localMeta, chat, refreshed);
            continue;
        }

        // Seed a lightweight local record. The body (items) is fetched on
        // activation via restoreTranscript; mark it so ChatConsole knows to
        // pull from the server rather than treating an empty body as truth.
        const localId = allocLocalId();
        const payload = {
            chatId: String(localId),
            schemaVersion: SCHEMA_VERSION,
            serverChatId: chat.chatId,
            kind: chat.kind ?? null,
            title: chat.title || "New chat",
            items: [],
            toggled: {},
            thinkingLevel: null,
            scopeMode: null,
            skipScope: false,
            messagePool: [],
            agentMessagesRef: [],
            canvas: null,
            smallToolResults: {},
            busyAtClose: false,
            savedAt: serverSavedAt(chat.updatedAt),
            // Restore markers (transient hints; harmless if persisted):
            needsServerBody: true,
            // What the SERVER says this chat holds. A restored stub carries no
            // items until it is activated, so without this it projects
            // hasItems:false and reads as a blank draft — which the sidebar
            // history filters out (CanvasMenu's startedChats). That is why a
            // fresh profile showed its chats one at a time, as each body
            // happened to load.
            serverTurnCount: chat.turnCount ?? 0,
            interrupted: !!chat.interrupted,
        };
        await saveChat(tenantId, localId, payload);
        // Seed the outbox at the server's revision so our first local edit
        // syncs as an update (not a create-conflict).
        await putOutboxEntry(tenantId, localId, {
            ...initialOutbox({ serverChatId: chat.chatId, now: Date.now() }),
            ackedRevision: chat.revision,
            // Seed the server turn high-water so a FIRST post-restore action that
            // rewinds (shrinks items[]) still tombstones the vacated server tail.
            // Without this it stays 0 and the shrink can't detect it must truncate.
            ackedTurnCount: chat.turnCount ?? 0,
            // Pin the dead-tail floor to 0 for this body-less stub: it must never
            // sync before restoreTranscript loads the body (guarded by dirty:false
            // + needsServerBody), but if it ever did, a 0 floor makes an empty
            // items[] send zero tombstones (head-only) instead of tombstoning the
            // entire server transcript. restoreTranscript resets it to items.length.
            tombstonedThrough: 0,
            // Everything on the server at restore time is already captured;
            // without this floor the first boundary re-publishes the chat's
            // whole history (idempotent, but pure waste and it inflates
            // chat_capture_exchanges_total).
            capturedThrough: Date.now(),
            dirty: false,
        });
        seeded.push({ localId, serverChatId: chat.chatId, title: payload.title, kind: payload.kind, interrupted: payload.interrupted });
    }
    // Mark this device "warm" (pages once from here on) ONLY when the list was
    // paged to exhaustion — a truncated first reconcile (hit the page ceiling
    // with more pending) must stay "cold" so the next mount re-pages the rest.
    // A warm reconcile is already complete and re-hits the ceiling every time;
    // it stays warm because the sentinel is already set.
    if (exhausted) markReconcileCompleted(tenantId);
    return { seeded, removed, refreshed };
}

// Fetch one chat's transcript from the server and write it into IDB. Returns
// the stored payload (with items assembled from turns) so the caller can drop
// it into React state. Blobs remain as placeholders until hydrateItemBlobs
// runs on card expand.
// Fetch a chat's server head + full transcript and assemble it into a storable
// payload, WITHOUT writing anything locally. Returns { payload, head } or null
// (chat gone / a page fetch failed — see the abort reasoning below). Split out
// of restoreTranscript so the conflict-resolution path can compare the server
// copy against the local one before deciding whether to overwrite (issue: a
// wholesale replace deletes local turns that are simply ahead of the server).
export async function fetchServerTranscript(serverChatId, localId) {
    if (!chatPersistenceEnabled() || !serverChatId) return null;
    let head = null;
    try {
        head = await getChat(serverChatId);
    } catch (err) {
        console.warn("[chatRestore] getChat failed:", err?.message || err);
        return null;
    }
    if (!head) return null;

    // Page turns newest-first; assemble ascending for the transcript.
    const allTurns = [];
    let before = null;
    for (let guard = 0; guard < 200; guard++) {
        let page;
        try {
            page = await listChatTurns(serverChatId, { beforeOrdinal: before, pageSize: TURN_PAGE_SIZE });
        } catch (err) {
            // Abort the WHOLE restore on a page failure (as reconcileChatList
            // does for a list failure). Saving the partial transcript and acking
            // it (dirty:false, ackedTurnCount = full server count) would both
            // permanently truncate visible history with no retry AND leave a gap
            // [items.length, ackedTurnCount) of never-fetched server turns that a
            // later local append would silently overwrite at a colliding ordinal.
            console.warn("[chatRestore] listChatTurns failed; aborting restore:", err?.message || err);
            return null;
        }
        if (!page.length) break;
        allTurns.push(...page);
        const minOrdinal = Math.min(...page.map((t) => t.turn_ordinal));
        if (minOrdinal <= 0 || page.length < TURN_PAGE_SIZE) break;
        before = minOrdinal;
    }

    const items = assembleItemsFromTurns(allTurns);
    const payload = {
        chatId: String(localId),
        schemaVersion: SCHEMA_VERSION,
        serverChatId,
        kind: head.kind ?? null,
        title: head.title || "New chat",
        items,
        toggled: head.itemUiState || {},
        thinkingLevel: head.thinkingLevel || null,
        scopeMode: head.scopeMode ?? null,
        skipScope: false,
        messagePool: [],
        agentMessagesRef: [],
        canvas: head.canvas ?? null,
        smallToolResults: {},
        busyAtClose: !!head.interrupted,
        savedAt: serverSavedAt(head.updatedAt),
        interrupted: !!head.interrupted,
        truncatedBeforeOrdinal: head.truncatedBeforeOrdinal ?? null,
    };
    return { payload, head };
}

// Seed the outbox for a chat whose LOCAL body now equals the server copy
// (adopt-server): align to the server revision + turn high-water and mark clean.
// Shared by restoreTranscript and the conflict resolver's adopt path.
export async function seedRestoredOutbox(tenantId, localId, payload, head) {
    const existing = await getOutboxEntry(tenantId, localId);
    await putOutboxEntry(tenantId, localId, {
        ...(existing || initialOutbox({ serverChatId: payload.serverChatId, now: Date.now() })),
        serverChatId: payload.serverChatId,
        ackedRevision: head.revision,
        // See reconcileChatList: seed the turn high-water so a first-action rewind
        // on this restored chat tombstones the discarded server tail.
        ackedTurnCount: head.turnCount ?? existing?.ackedTurnCount ?? 0,
        // Reset the dead-tail floor to the live prefix length, overriding any
        // stale value the `...existing` spread carried. Rewind tombstones are
        // always a contiguous tail, so every ordinal >= items.length is already
        // dead server-side; leaving a lower stale floor would make a subsequent
        // shrink under-tombstone and resurrect the tail on the next restore.
        tombstonedThrough: (payload.items || []).length,
        // Seed the delta fingerprints from the just-restored turns so the first
        // post-restore boundary doesn't re-POST an unchanged transcript. Goes
        // through the same buildTurns path the engine hashes, so the hashes match.
        turnHashes: buildTurnHashes(payload),
        // Capture's equivalent of the turnHashes seed above: the server already
        // holds every call in this transcript, so the first post-restore
        // boundary must not re-publish them.
        capturedThrough: Date.now(),
        conflict: false,
        dirty: false,
        lastError: null,
    });
}

export async function restoreTranscript(tenantId, localId, serverChatId) {
    const fetched = await fetchServerTranscript(serverChatId, localId);
    if (!fetched) return null;
    await saveChat(tenantId, localId, fetched.payload);
    await seedRestoredOutbox(tenantId, localId, fetched.payload, fetched.head);
    return fetched.payload;
}

// Fresh-device restore gate for a lazily-loaded chat body. When persistence is
// on and the local record is a server-seeded stub (`serverChatId` +
// `needsServerBody`), pull its transcript from PG before the caller hydrates.
// Returns true → proceed to read/hydrate the (now-restored) body; false → the
// restore failed/was unreachable and the caller MUST re-arm the load and bail
// rather than caching the empty stub as final (serialize drops needsServerBody,
// and reconcile only reseeds chats with no local meta, so a stub saved without
// the marker could never be re-fetched). Deps injected so it's unit-testable
// without the component's refs. `deps`: { loadChat, restoreTranscript }.
export async function restoreServerBodyIfNeeded({ id, tenantId, persistenceOn, deps }) {
    if (!persistenceOn) return true;
    try {
        const raw = await deps.loadChat(tenantId, id);
        if (raw?.serverChatId && raw.needsServerBody) {
            const restored = await deps.restoreTranscript(tenantId, id, raw.serverChatId);
            if (!restored) return false;
        }
        return true;
    } catch {
        return false;
    }
}

// Merge blob-hydrated items back into the current items array by OBJECT
// IDENTITY. Hydration runs async while the chat stays continuable, so a
// wholesale replace would clobber turns appended meanwhile; folding by identity
// leaves any item not in `hydratedByOriginal` (an append, or a user-replaced
// turn — a new object) untouched. Returns the SAME array reference when nothing
// changed, so the caller can skip a re-render. Pure + exported for unit testing.
export function mergeHydratedItems(items, hydratedByOriginal) {
    if (!Array.isArray(items) || !hydratedByOriginal || hydratedByOriginal.size === 0) return items;
    let changed = false;
    const merged = items.map((it) => {
        const hyd = hydratedByOriginal.get(it);
        if (hyd) { changed = true; return hyd; }
        return it;
    });
    return changed ? merged : items;
}

// Lazily hydrate a render document's blob placeholders by streaming each blob
// back through the authenticated read route. Used on card expand.
//
// Re-derives image preview URLs afterwards: `restoreImagePreviewUrls` runs once
// at load time, when an externalized image's `data` is still a placeholder
// OBJECT, so it declines and the image has no `src`. The bytes arriving here is
// the moment it becomes derivable. Idempotent, so an image whose data was small
// enough to stay inline (already given its previewUrl at load) is untouched.
//
// `onHydrated` is forwarded per blob so the caller can record what came back —
// see recordHydratedBlobs in the sync engine.
export async function hydrateItemBlobs(serverChatId, renderDocument, { onHydrated } = {}) {
    if (!hasBlobPlaceholders(renderDocument)) return renderDocument;
    const hydrated = await hydrateBlobPlaceholders(
        renderDocument,
        (blobId, meta) => readBlob(serverChatId, blobId, { contentType: meta?.contentType }),
        { onHydrated },
    );
    return restoreImagePreviewUrls([hydrated])[0];
}

// Run `fn` over `items` with at most `limit` concurrent in flight — a bounded
// worker pool. Prevents an unbounded Promise.all from firing one network call
// per item at once (and holding every result resident). `fn(item, index)` is
// awaited; a throw rejects the whole run (callers that want best-effort catch
// inside `fn`). Exported + pure so the bound is unit-testable.
export async function mapWithConcurrency(items, limit, fn) {
    const arr = Array.isArray(items) ? items : [];
    const cap = Math.min(Math.max(1, limit | 0), arr.length);
    let next = 0;
    const worker = async () => {
        while (next < arr.length) {
            const i = next++;
            await fn(arr[i], i);
        }
    };
    await Promise.all(Array.from({ length: cap }, () => worker()));
}

// Hydrate the blob placeholders across a restored snapshot's items with a
// BOUNDED worker pool. A restored chat can carry many externalized blobs
// (frozen tables, pasted images); an unbounded Promise.all would fire one
// authenticated GET per blob at once and hold every decoded blob resident, so
// `concurrency` caps both in-flight reads and peak memory. Returns a Map of
// original item → hydrated item for the placeholder-bearing items ONLY, so the
// caller merges by object identity (never replacing the live list) and can't
// clobber a turn appended while this ran. Best-effort per item is the caller's
// concern; a throw here rejects the batch (the caller leaves placeholders).
export async function hydrateItemsBounded(serverChatId, items, concurrency = 4, { onHydrated } = {}) {
    const out = new Map();
    const placeholders = (Array.isArray(items) ? items : []).filter((it) => it && hasBlobPlaceholders(it));
    await mapWithConcurrency(placeholders, concurrency, async (it) => {
        out.set(it, await hydrateItemBlobs(serverChatId, it, { onHydrated }));
    });
    return out;
}

export { hasBlobPlaceholders };
